import styled from "styled-components";
import { ContentCard } from "../components/ContentCard";
import { StyledLink, Wrapper } from "../styles";
import { useAuthCheck } from "../../../hooks/useAuthCheck";
import api from "../../../util/api";
import { SubscriptionOptionEnum } from "@fena/toolkit-types";
import { AppRoutes } from "../../../constants/routes";
import { useNavigate } from "react-router-dom";
import { usePartnerLayoutContext } from "../../../context/PartnerLayoutContextProvider";
import {
  Avatar,
  AvatarSizeVariant,
  Button,
  ButtonVariant,
  Spinner,
  Typography,
  TypographyVariant,
} from "../../../components/ui-kit";
import { useEffect, useState } from "react";
import { LastLoginData } from "../../../util/types";
import { DateTime } from "luxon";
import { Auth } from "aws-amplify";
import { ArrowDownGrey } from "../../../components/icons";
import { useUserContext } from "../../../context/UserContextProvider";
import { useCompanyStore } from "../../../stores/useCompanyStore";

const StyledWrapper = styled(Wrapper)`
  background-color: #f8f9fa;
`;

const Content = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

const StyledSpinner = styled(Spinner)`
  margin: 40px 0 60px;
`;

const UserInfoContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid #eee;
  padding: 10px;
  border-radius: 5px;
  margin: 50px 0 15px;
  cursor: pointer;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
`;

const TextInfo = styled.div`
  margin-left: 15px;
`;

const Email = styled(Typography)`
  margin-bottom: 10px;
`;

const LastUsed = styled(Typography)``;

const ArrorRight = styled(ArrowDownGrey)`
  transform: rotate(-90deg);
`;

const GreenButton = styled(Button)`
  background-color: #2cd19e;
  color: #fff;

  &:hover {
    background-color: #2cd19e;
  }
`;

const BlueButton = styled(Button)`
  margin-top: 15px;
`;

const StyledTypography = styled(Typography)`
  text-align: center;
  margin-top: 35px;
`;

export const CheckSignIn: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const { checkSignInStatus } = useAuthCheck();
  const { isPartnerMode, setIsPartnerMode } = usePartnerLayoutContext();
  const { company, clearCompany } = useCompanyStore();
  const { user } = useUserContext();
  const { clearUser } = useUserContext();
  const [shouldLogOut, setShouldLogOut] = useState(false);
  const [continueLoading, setContinueLoading] = useState(false);

  const [loading, setLoading] = useState(false);

  const [showSession, setShowSession] = useState(false);

  const checkSubscription = async () => {
    try {
      const currentSubscription = await api.getCurrentSubscriptions();

      return (
        currentSubscription.data?.[0].details?.subscriptionOption ??
        currentSubscription.data?.[0]?.subscriptionOption
      );
    } catch (e) {
      return null;
    }
  };

  const logOut = async () => {
    await Auth.signOut();
    clearUser();
    clearCompany();
    window.localStorage.removeItem("selectedCompany");
    navigate(AppRoutes.Open.Auth.SIGN_IN_START);
  };

  const checkAndNavigateToDestination = (
    canManageCompanies: boolean,
    isPartnerMode: boolean,
    isManagedByPartner: boolean,
    subscriptionType: SubscriptionOptionEnum
  ) => {
    let destination;
    if (canManageCompanies && isPartnerMode) {
      destination = AppRoutes.Private.Partner.DASHBOARD;
      setIsPartnerMode(true);
    } else {
      if (
        subscriptionType === SubscriptionOptionEnum.PAYMENTS_ONLY ||
        isManagedByPartner
      ) {
        destination = AppRoutes.Private.Payments.TRANSACTIONS_LIST;
      } else if (
        location.pathname === AppRoutes.Open.Auth.SIGN_IN_START &&
        subscriptionType === SubscriptionOptionEnum.SHOPIFY_B2B_SUBSCRIPTION
      ) {
        destination = AppRoutes.Private.Apps.HOME;
      } else if (window.innerWidth <= 576) {
        destination = AppRoutes.Private.Inventory.ORDER_GENERATION_EPOS;
      } else {
        destination = AppRoutes.Private.Inventory.ORDERS_LIST;
      }
    }
    navigate(destination);
  };

  const asyncCheck = async () => {
    try {
      setLoading(true);

      let loginEvents: LastLoginData[] = [];

      try {
        loginEvents = await api.getLoginEvents();
      } catch (error) {
        console.error("Failed to fetch login events", error);
        await Auth.signOut();
        navigate(AppRoutes.Open.Auth.SIGN_IN_START);
        return;
      }

      const sortedLogins = loginEvents
        ?.filter((e) => e.eventType === "LOGIN")
        ?.sort(
          (a, b) =>
            new Date(b.createdAt)?.getTime() - new Date(a.createdAt)?.getTime()
        );

      const lastLogin = sortedLogins[0]?.createdAt;

      if (lastLogin) {
        const loginTime = DateTime.fromISO(lastLogin);
        const now = DateTime.now();
        const hoursAgo = now.diff(loginTime, "hours").hours;

        if (hoursAgo >= 1 && hoursAgo <= 8) {
          setShowSession(true);
          return;
        } else if (hoursAgo >= 8) {
          setShowSession(true);
          setShouldLogOut(true);
        } else {
          const { user, companyData } = await checkSignInStatus();

          if (user) {
            const subscriptionType = await checkSubscription();
            const isManagedByPartner = !!companyData.managedBy;

            checkAndNavigateToDestination(
              companyData?.canManageCompanies,
              isPartnerMode,
              isManagedByPartner,
              subscriptionType
            );
          }

          setLoading(false);
        }
      } else {
        await Auth.signOut();
        navigate(AppRoutes.Open.Auth.SIGN_IN_START);
      }
    } catch (e) {
      console.error("Unexpected error in asyncCheck", e);

      setLoading(false);
    }
  };

  useEffect(() => {
    asyncCheck();
  }, []);

  const handleContinue = async () => {
    if (shouldLogOut) {
      logOut();
      const params = new URLSearchParams();
      if (user?.email) {
        params.set("email", user.email);
      }
      navigate(AppRoutes.Open.Auth.SIGN_IN_START, {
        state: { email: user?.email },
      });
    } else {
      setContinueLoading(true);
      const subscriptionType = await checkSubscription();
      const isManagedByPartner = !!company?.managedBy;

      checkAndNavigateToDestination(
        company?.canManageCompanies ?? false,
        isPartnerMode,
        isManagedByPartner,
        subscriptionType
      );
    }
    setContinueLoading(false);
  };
  return (
    <StyledWrapper>
      <ContentCard title="Sign in" disableEndSession>
        {showSession ? (
          <>
            <UserInfoContainer onClick={handleContinue}>
              <UserInfo>
                <Avatar
                  size={AvatarSizeVariant.SMALL}
                  avatarSrc={user?.avatar?.url}
                  disableDropdown
                />
                <TextInfo>
                  <Email variant={TypographyVariant.BODY1}>{user?.email}</Email>
                  <LastUsed variant={TypographyVariant.BODY7} color="#8181A5">
                    Last used account:{" "}
                    {user?.lastActivity
                      ? DateTime.fromISO(
                          user?.lastActivity?.toString() ?? ""
                        ).toFormat("dd.MM.yyyy : HH:mm")
                      : ""}
                  </LastUsed>
                </TextInfo>
              </UserInfo>
              <ArrorRight />
            </UserInfoContainer>

            <GreenButton
              fullWidth
              loading={continueLoading}
              onClick={() => {
                handleContinue();
              }}
            >
              Continue with this session
            </GreenButton>

            <StyledTypography variant={TypographyVariant.BODY1}>
              Want to use another account?
            </StyledTypography>
            <BlueButton
              fullWidth
              variant={ButtonVariant.CONTAINED}
              onClick={logOut}
            >
              Sign in with different account
            </BlueButton>
            <StyledTypography variant={TypographyVariant.BODY1}>
              Want to use another account?{" "}
              <StyledLink to={AppRoutes.Open.Auth.SIGN_UP_START}>
                Sign up
              </StyledLink>
            </StyledTypography>
          </>
        ) : (
          <Content>
            {loading && <StyledSpinner size={60} />}

            <Typography>We are verifying your session</Typography>
          </Content>
        )}
      </ContentCard>
    </StyledWrapper>
  );
};
